import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Target, Sparkles, Lightbulb } from "lucide-react";
import client, { errText } from "../../lib/api";
import Stepper from "./Stepper";

const POSTURES = [
  { key: "curious",    label: "curious",    body: "I want to see what shows up" },
  { key: "decided",    label: "decided",    body: "I have a direction and want a check" },
  { key: "stuck",      label: "stuck",      body: "I am not sure what to try next" },
  { key: "searching",  label: "searching",  body: "I am comparing a few paths" },
];

// Shown instantly so the presets are never empty. The suggestions
// fetch upgrades this to a field-relevant set when it resolves.
const FALLBACK_GOALS = [
  "what i actually want to do after college",
  "whether i'm on the right path or should switch",
  "which of my options is the real one for me",
  "what i'm good at that i could build on",
];

/**
 * A8: Goal + posture. Free-text goal, one posture from four. The
 * posture becomes the tone hint Klair uses in her narrative turns.
 */
export default function GoalPostureScreen({ product }) {
  const nav = useNavigate();
  const base = product === "grow" ? "/grow" : "";
  const [goal, setGoal] = useState("");
  const [posture, setPosture] = useState("curious");
  const [presets, setPresets] = useState(FALLBACK_GOALS);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [triageHint, setTriageHint] = useState("");
  const triageTimer = useRef(null);

  // KLA-043 at the front door: a gentle, non-blocking read of what
  // they typed. A question or a wish gets an honest heads up; typing
  // anything remains allowed.
  useEffect(() => {
    if (triageTimer.current) clearTimeout(triageTimer.current);
    const text = goal.trim();
    if (text.length < 8) { setTriageHint(""); return; }
    triageTimer.current = setTimeout(async () => {
      try {
        const { data } = await client.get(`/onboarding/goal/triage?text=${encodeURIComponent(text.slice(0, 400))}`);
        setTriageHint(data?.hint || "");
      } catch (_e) { setTriageHint(""); }
    }, 700);
    return () => { if (triageTimer.current) clearTimeout(triageTimer.current); };
  }, [goal]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/onboarding/goal/suggestions");
        if (Array.isArray(data?.goals) && data.goals.length) {
          setPresets(data.goals);
        }
      } catch (_e) {
        // keep the fallback presets so the row is never empty
      }
      try {
        const { data } = await client.get("/onboarding");
        if (data.goal) setGoal(data.goal);
        if (data.posture) setPosture(data.posture);
      } catch (_e) { /* first time */ }
    })();
  }, []);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const existing = await client.get("/onboarding");
      const cur = existing?.data || {};
      const payload = {
        org_slug: cur.org_slug || null,
        education: cur.education || [],
        experience_chips: cur.experience_chips || [],
        goal: goal.trim(),
        posture: posture,
        step_index: 4,
        is_complete: true,
      };
      await client.put("/onboarding", payload);
      // Mirror into a lightweight local intake so the conversation
      // orchestrator can warm-start.
      localStorage.setItem("klareet.intake", JSON.stringify({
        goal: payload.goal,
        posture: payload.posture,
        chips: (payload.experience_chips || []).join(", "),
        institution: (payload.education?.[0]?.institution) || "",
        programme: (payload.education?.[0]?.programme) || "",
      }));
      nav(`${base}/klair`);
    } catch (e) {
      setErr(errText(e, "That did not save. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="k-shell relative overflow-hidden">
      <div className="k-orb k-orb-blue"  style={{ top: "-6rem", left: "-6rem",  width: "20rem", height: "20rem" }} />
      <div className="k-orb k-orb-teal"  style={{ bottom: "-6rem", right: "-6rem", width: "22rem", height: "22rem" }} />
      <div className="relative max-w-2xl mx-auto px-5 pt-10 pb-24">
        <Stepper current={3} total={3} />
        <div className="mt-6 k-fade-in">
          <div className="k-chip k-chip-warn mb-3"><Target size={12} /> goal and posture</div>
          <h1 className="klareet-serif mb-3" style={{ fontSize: "clamp(1.7rem, 3.6vw, 2.3rem)", lineHeight: 1.12 }} data-testid="goal-title">
            What are you hoping to figure out?
          </h1>
          <p className="k-quiet mb-6">Just one line, rough is totally fine. It helps me ask you the right things.</p>
        </div>

        <div className="k-card k-fade-in" style={{ animationDelay: "60ms" }}>
          <label className="k-label">Your goal</label>
          <textarea
            className="k-input mt-1 klareet-serif"
            rows={3}
            maxLength={400}
            placeholder="I want to figure out ..."
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            data-testid="goal-text"
          />
          <div className="k-quiet !text-xs mt-1">{goal.length} of 400</div>

          {triageHint && (
            <div
              className="mt-3 rounded-xl p-3 flex items-start gap-2.5"
              style={{ background: "hsl(var(--klareet-amber) / 0.10)", border: "1px solid hsl(var(--klareet-amber) / 0.28)" }}
              data-testid="goal-triage-hint"
            >
              <Lightbulb size={15} className="shrink-0 mt-0.5" style={{ color: "hsl(var(--klareet-amber))" }} />
              <div className="text-sm" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }}>{triageHint}</div>
            </div>
          )}

          <div className="mt-4">
            <div className="k-label mb-2 flex items-center gap-1.5">
              <Sparkles size={12} /> Not sure? Tap one to start
            </div>
            <div className="flex flex-wrap gap-2" data-testid="goal-presets">
              {presets.map((g) => {
                const active = goal.trim() === g;
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGoal(g)}
                    className="k-pill text-left"
                    style={active ? {
                      background: "hsl(var(--klareet-blue-soft))",
                      color: "hsl(var(--klareet-blue-strong))",
                      borderColor: "hsl(var(--klareet-blue))",
                      fontWeight: 600,
                    } : {}}
                    data-testid={`goal-preset-${g.replace(/[^a-z0-9]+/gi, "-").slice(0, 40)}`}
                  >
                    {g}
                  </button>
                );
              })}
            </div>
            <div className="k-quiet !text-xs mt-2">Pick one and tweak it, or write your own above.</div>
          </div>
        </div>

        <div className="mt-5 k-fade-in" style={{ animationDelay: "120ms" }}>
          <div className="k-label mb-2">And how are you feeling about it right now?</div>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="posture-choices">
            {POSTURES.map((p) => {
              const active = posture === p.key;
              return (
                <button
                  key={p.key}
                  onClick={() => setPosture(p.key)}
                  className="text-left k-card k-lift"
                  style={{
                    padding: "1rem 1.1rem",
                    border: active ? "1px solid hsl(var(--klareet-blue))" : "1px solid hsl(var(--klareet-line-soft))",
                    background: active ? "hsl(var(--klareet-blue-soft))" : "hsl(var(--card))",
                  }}
                  data-testid={`posture-${p.key}`}
                >
                  <div className="font-semibold" style={{ color: active ? "hsl(var(--klareet-blue-strong))" : "hsl(var(--klareet-ink))" }}>
                    {p.label}
                  </div>
                  <div className="k-quiet mt-1">{p.body}</div>
                </button>
              );
            })}
          </div>
        </div>

        {err && <div className="mt-4 k-quiet" style={{ color: "hsl(var(--destructive))" }}>{err}</div>}

        <div className="mt-8 flex justify-between items-center">
          <button onClick={() => nav(`${base}/onboarding/experience`)} className="k-btn k-btn-ghost" data-testid="goal-back">
            Back
          </button>
          <button onClick={submit} disabled={!goal.trim() || busy} className="k-btn k-btn-accent" data-testid="goal-continue">
            {busy ? "Saving." : "Okay, let us talk"} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
